import React from 'react';
import { motion } from 'framer-motion';
import { ThumbsUp, MessageSquare, ExternalLink, Linkedin } from 'lucide-react';
import news from './newsData';
import logoColored from '../../assets/logoColored.svg';

const LINKEDIN_URL = 'https://www.linkedin.com/company/geoglobalgate-ltd';

const News = () => {
  if (!news || news.length === 0) return null;

  return (
    <section id="news">
      <style>{`
        .news-section {
          background: linear-gradient(135deg, #0f2350 0%, #1b3f82 50%, #2563eb 100%);
          border-top: 1px solid rgba(71,146,244,0.30);
          padding: 5rem 2rem;
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 3rem;
        }

        .news-header {
          text-align: center;
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 0.75rem;
        }
        .news-badge {
          display: inline-flex; align-items: center; gap: 0.5rem;
          background: rgba(71,146,244,0.18);
          border: 1px solid rgba(71,146,244,0.45);
          border-radius: 50px; padding: 0.3rem 1rem;
          font-size: 0.78rem; font-weight: 700; color: #a8c8ff;
          letter-spacing: 0.1em; text-transform: uppercase;
        }
        .news-title {
          font-size: clamp(1.6rem, 3vw, 2.4rem);
          font-weight: 700;
          color: #ffffff;
          margin: 0;
          letter-spacing: -0.02em;
        }
        .news-subtitle {
          font-size: 1rem;
          color: #c0d8ff;
          margin: 0;
        }

        /* Grid */
        .news-grid {
          max-width: 1100px;
          width: 100%;
          display: grid;
          grid-template-columns: repeat(4, 1fr);
          gap: 1.1rem;
        }

        /* Card */
        .news-card {
          background: #fff;
          border-radius: 12px;
          overflow: hidden;
          display: flex;
          flex-direction: column;
          box-shadow: 0 4px 18px rgba(0,0,0,0.16);
          text-decoration: none;
          transition: box-shadow 0.2s, transform 0.2s;
          cursor: pointer;
        }
        .news-card:hover {
          box-shadow: 0 10px 32px rgba(0,0,0,0.24);
          transform: translateY(-5px);
        }

        /* Card header (profile) */
        .news-card-profile {
          display: flex; align-items: center; gap: 0.55rem;
          padding: 0.75rem 0.85rem 0.5rem;
        }
        .news-profile-logo {
          width: 34px; height: 34px; border-radius: 6px;
          background: #f0f4ff;
          display: flex; align-items: center; justify-content: center;
          flex-shrink: 0; overflow: hidden;
        }
        .news-profile-logo img { width: 26px; height: 26px; object-fit: contain; }
        .news-profile-info { display: flex; flex-direction: column; gap: 1px; min-width: 0; }
        .news-profile-name {
          font-size: 0.75rem; font-weight: 700; color: #0f1f40;
          line-height: 1.2; white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
        }
        .news-profile-handle {
          font-size: 0.65rem; color: #0077b5;
          white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
        }

        /* Image */
        .news-card-img-wrap {
          width: 100%; aspect-ratio: 4/3; overflow: hidden; flex-shrink: 0;
        }
        .news-card-img-wrap img {
          width: 100%; height: 100%; object-fit: cover; display: block;
          transition: transform 0.4s ease;
        }
        .news-card:hover .news-card-img-wrap img { transform: scale(1.05); }

        /* Body */
        .news-card-body {
          padding: 0.75rem 0.85rem;
          display: flex; flex-direction: column; gap: 0.4rem;
          flex: 1;
        }
        .news-card-title-text {
          font-size: 0.82rem; font-weight: 700; color: #0f1f40;
          line-height: 1.4; margin: 0;
          display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical;
          overflow: hidden;
        }
        .news-card-excerpt {
          font-size: 0.78rem; color: #4a6080; line-height: 1.55; margin: 0;
          display: -webkit-box; -webkit-line-clamp: 3; -webkit-box-orient: vertical;
          overflow: hidden;
        }

        /* Footer */
        .news-card-footer {
          padding: 0.55rem 0.85rem 0.75rem;
          display: flex; align-items: center; gap: 0.85rem;
          border-top: 1px solid #f0f0f0;
          margin-top: auto;
        }
        .news-stat {
          display: flex; align-items: center; gap: 0.28rem;
          font-size: 0.72rem; color: #666; font-weight: 500;
        }
        .news-view {
          margin-left: auto;
          display: flex; align-items: center; gap: 0.28rem;
          font-size: 0.72rem; color: #0077b5; font-weight: 600;
        }

        /* LinkedIn CTA */
        .news-linkedin-cta {
          display: inline-flex; align-items: center; gap: 0.6rem;
          background: #0a66c2; color: #ffffff;
          font-size: 0.92rem; font-weight: 600;
          padding: 0.7rem 1.5rem;
          border-radius: 50px; text-decoration: none;
          box-shadow: 0 4px 14px rgba(10,102,194,0.3);
          transition: background 0.2s, transform 0.2s;
        }
        .news-linkedin-cta:hover { background: #004182; transform: translateY(-2px); }

        @media (max-width: 900px) {
          .news-grid { grid-template-columns: repeat(2, 1fr); }
        }
        @media (max-width: 500px) {
          .news-grid { grid-template-columns: 1fr; }
        }
      `}</style>

      <div className="news-section">

        <motion.div
          className="news-header"
          initial={{ opacity: 0, y: -20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <div className="news-badge">
            <Linkedin size={13} />
            GeoGlobalGate News
          </div>
          <h2 className="news-title">Stay up to date with our latest news and events.</h2>
          <p className="news-subtitle">Follow us on LinkedIn for updates</p>
        </motion.div>

        <div className="news-grid">
          {news.map((item, i) => (
            <motion.a
              key={i}
              className="news-card"
              href={item.link}
              target="_blank"
              rel="noopener noreferrer"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: i * 0.1 }}
            >
              <div className="news-card-profile">
                <div className="news-profile-logo">
                  <img src={logoColored} alt="GeoGlobalGate" />
                </div>
                <div className="news-profile-info">
                  <span className="news-profile-name">GeoGlobalGate LTD</span>
                  <span className="news-profile-handle">linkedin.com/company/…</span>
                </div>
              </div>

              <div className="news-card-img-wrap">
                <img src={item.image} alt={item.title} />
              </div>

              <div className="news-card-body">
                <p className="news-card-title-text">{item.title}</p>
                <p className="news-card-excerpt">{item.excerpt}</p>
              </div>

              <div className="news-card-footer">
                <span className="news-stat">
                  <ThumbsUp size={12} color="#0077b5" /> {item.likes}
                </span>
                <span className="news-stat">
                  <MessageSquare size={12} color="#666" /> {item.comments}
                </span>
                <span className="news-view">
                  <ExternalLink size={11} /> View
                </span>
              </div>
            </motion.a>
          ))}
        </div>

        <motion.a
          className="news-linkedin-cta"
          href={LINKEDIN_URL}
          target="_blank"
          rel="noopener noreferrer"
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
        >
          <Linkedin size={16} />
          View all posts on LinkedIn
        </motion.a>

      </div>
    </section>
  );
};

export default News;
